import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Copy, Download, FileText, RefreshCw, Users, GraduationCap, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import TrainingReports from './TrainingReports';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [30, 60, 90, 180, 365];

const rel = (days) => {
  if (days == null) return '';
  if (days < 0) { const d = -days; return d < 60 ? `${d}d ago` : `${Math.round(d / 30)} mo ago`; }
  return days < 60 ? `in ${days}d` : `in ${Math.round(days / 30)} mo`;
};

const openCert = async (fileId) => {
  try {
    const r = await fetch(`${API}/certificate-file/${fileId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!r.ok) throw new Error();
    const url = URL.createObjectURL(await r.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error('Could not open that file'); }
};

const Pill = ({ status, expiry, days }) => status === 'missing' ? (
  <span className="text-xs px-2 py-0.5 rounded-full border whitespace-nowrap bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" title="No card of this kind on file at all">Never had one</span>
) : (
  <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${status === 'expired' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
    {status === 'expired' ? 'Expired' : 'Expires'} {expiry || '—'}{days != null ? ` · ${rel(days)}` : ''}
  </span>
);
const rowBg = (status) => status === 'expired' ? 'bg-red-50' : status === 'missing' ? 'bg-fuchsia-50' : 'bg-amber-50';

const TicketRef = ({ ticket, fileId }) => (
  fileId
    ? <button onClick={() => openCert(fileId)} className="text-xs text-slate-500 hover:text-blue-700 underline decoration-dotted truncate max-w-[280px] text-left" title={`Open “${ticket}” from the Drive folder`}>{ticket}</button>
    : <span className="text-xs text-slate-500 truncate max-w-[280px]" title={ticket}>{ticket}</span>
);

const CourseCard = ({ t }) => {
  const [open, setOpen] = useState(true);
  const copy = () => {
    navigator.clipboard.writeText(`${t.type}\n` + t.employees.map((e) => `${e.name} — ${e.status === 'missing' ? 'never had one' : `${e.status === 'expired' ? 'expired' : 'expires'} ${e.expiry || '?'}`}`).join('\n'));
    toast.success(`Copied ${t.employees.length} name${t.employees.length !== 1 ? 's' : ''} for ${t.type}`);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`mt-course-${t.topic}`}>
      <div className="flex items-center justify-between gap-3 p-4">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left min-w-0" data-testid={`mt-course-toggle-${t.topic}`}>
          {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
          <h3 className="text-slate-900 font-semibold truncate">{t.type}</h3>
          <span className="text-xs text-slate-500 shrink-0">{t.employees.length} {t.employees.length === 1 ? 'person' : 'people'}</span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {t.missing > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200" data-testid={`mt-course-missing-${t.topic}`}>{t.missing} never had one</span>}
          {t.expired > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">{t.expired} expired</span>}
          {t.expiring_90 > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{t.expiring_90} expiring</span>}
          <button onClick={copy} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100" title="Copy the names for this course" data-testid={`mt-course-copy-${t.topic}`}><Copy className="w-4 h-4" /></button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-1">
          {t.employees.map((e) => (
            <div key={`${e.employee_id}-${e.ticket_id}`} className={`flex flex-wrap items-center justify-between gap-2 text-sm px-3 py-1.5 rounded-lg ${rowBg(e.status)}`} data-testid={`mt-row-${t.topic}-${e.employee_id}`}>
              <span className="font-medium text-slate-800">{e.name}</span>
              <span className="flex items-center gap-2 min-w-0">
                <TicketRef ticket={e.ticket} fileId={e.drive_file_id} />
                <Pill status={e.status} expiry={e.expiry} days={e.days} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PersonCard = ({ p }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`mt-person-${p.employee_id}`}>
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="min-w-0">
        <h3 className="text-slate-900 font-semibold truncate">{p.name}</h3>
        <p className="text-xs text-slate-500">{(p.role || '').replace(/_/g, ' ')}{p.hidden_older ? ` · ${p.hidden_older} older card${p.hidden_older !== 1 ? 's' : ''} replaced` : ''}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        {p.no_tickets_on_file && <span className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 font-semibold" data-testid={`mt-person-notickets-${p.employee_id}`}>NO TICKETS ON FILE</span>}
        {p.missing > 0 && !p.no_tickets_on_file && <span className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">{p.missing} never had</span>}
        {p.expired > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">{p.expired} expired</span>}
        {p.expiring > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{p.expiring} expiring</span>}
      </div>
    </div>
    <div className="space-y-1">
      {p.renewals.map((r) => (
        <div key={r.topic} className={`flex flex-wrap items-center justify-between gap-2 text-sm px-3 py-1.5 rounded-lg ${rowBg(r.status)}`}>
          <span className="font-medium text-slate-800">{r.label}</span>
          <span className="flex items-center gap-2 min-w-0">
            <TicketRef ticket={r.ticket} fileId={r.drive_file_id} />
            <Pill status={r.status} expiry={r.expiry} days={r.days} />
          </span>
        </div>
      ))}
    </div>
  </div>
);

// Safety & Training → "Mass Training": who needs which renewal, grouped by course (to book) or by person (to check the folder).
export default function MassTrainingPanel() {
  const [days, setDays] = useState(90);
  const [view, setView] = useState('course');
  const [includeStale, setIncludeStale] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d = days, stale = includeStale) => {
    setLoading(true);
    try { const r = await axios.get(`${API}/certificates/mass-training-summary`, { params: { days: d, stale_years: stale ? 0 : 3 } }); setData(r.data); }
    catch { toast.error('Failed to load renewals'); }
    setLoading(false);
  };
  useEffect(() => { load(days, includeStale); }, [days, includeStale]); // eslint-disable-line

  const csv = useMemo(() => {
    if (!data) return '';
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['Course', 'Employee', 'Status', 'Expiry', 'Ticket on file']];
    data.training_needs.forEach((t) => t.employees.forEach((e) => rows.push([t.type, e.name, e.status, e.expiry || '', e.ticket])));
    return rows.map((r) => r.map(esc).join(',')).join('\n');
  }, [data]);

  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `renewals-due-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4 mb-8" data-testid="mass-training-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div>
          <p className="text-amber-700 font-semibold text-sm flex items-center gap-2" data-testid="mt-headline">
            <AlertTriangle className="w-4 h-4" />
            {data ? `${data.total_people} ${data.total_people === 1 ? 'person needs' : 'people need'} renewals across ${data.total_types_needing_attention} course${data.total_types_needing_attention !== 1 ? 's' : ''}` : 'Loading…'}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            Expired or expiring within {days} days. One line per course per person — the newest card in the folder decides.
            {data?.hidden_older > 0 && <span data-testid="mt-hidden-note"> {data.hidden_older} older / duplicate cards are hidden because a newer card replaces them.</span>}
          </p>
          {data?.no_tickets_on_file?.length > 0 && (
            <p className="text-xs text-fuchsia-700 mt-1" data-testid="mt-no-tickets-note">
              <b>{data.no_tickets_on_file.length}</b> {data.no_tickets_on_file.length === 1 ? 'person has' : 'people have'} no tickets on file at all: {data.no_tickets_on_file.join(', ')} — they're listed as “never had one” for everything their role requires.
            </p>
          )}
          {data?.long_expired_hidden > 0 && !includeStale && (
            <button onClick={() => setIncludeStale(true)} className="text-xs text-slate-500 hover:text-slate-900 underline decoration-dotted mt-1" data-testid="mt-show-stale">
              {data.long_expired_hidden} card{data.long_expired_hidden !== 1 ? 's' : ''} that lapsed more than 3 years ago left out — show them
            </button>
          )}
          {includeStale && (
            <button onClick={() => setIncludeStale(false)} className="text-xs text-slate-500 hover:text-slate-900 underline decoration-dotted mt-1" data-testid="mt-hide-stale">
              Including cards that lapsed 3+ years ago — hide them again
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-300" data-testid="mt-window">
            {WINDOWS.map((w) => (
              <button key={w} onClick={() => setDays(w)} className={`px-2.5 py-1.5 text-xs font-semibold ${days === w ? 'bg-amber-500 text-slate-900' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid={`mt-window-${w}`}>{w}d</button>
            ))}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-slate-300" data-testid="mt-view">
            <button onClick={() => setView('course')} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${view === 'course' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid="mt-view-course"><GraduationCap className="w-3.5 h-3.5" /> By course</button>
            <button onClick={() => setView('person')} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${view === 'person' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid="mt-view-person"><Users className="w-3.5 h-3.5" /> By person</button>
            <button onClick={() => setView('trainer')} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${view === 'trainer' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid="mt-view-trainer"><Building2 className="w-3.5 h-3.5" /> By trainer</button>
          </div>
          <button onClick={download} disabled={!data} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-800 hover:bg-slate-100 flex items-center gap-1" data-testid="mt-download-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
          <button onClick={() => load(days, includeStale)} className="p-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100" title="Refresh" data-testid="mt-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {view === 'trainer' ? <TrainingReports days={days} includeStale={includeStale} /> : !data ? <p className="text-slate-500 text-center py-8">Loading...</p> : data.training_needs.length === 0 ? (
        <div className="text-center py-10 text-slate-500" data-testid="mt-empty"><FileText className="w-10 h-10 mx-auto mb-2 text-slate-600" />Nothing expired or expiring within {days} days.</div>
      ) : view === 'course' ? (
        <div className="space-y-3" data-testid="mt-by-course">{data.training_needs.map((t) => <CourseCard key={t.topic} t={t} />)}</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="mt-by-person">{data.by_person.map((p) => <PersonCard key={p.employee_id} p={p} />)}</div>
      )}
    </div>
  );
}
